/**
 * AccountClosedHandler - Global handler for 403 ACCOUNT_SUSPENDED responses
 *
 * Listens for 'auth:account-closed' events dispatched by apiFetch.
 * Signs the user out and shows the closed-account message.
 * Separate from session-expired (401) which has different UX.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './ui/Toast';

function readAuthErrorParams(): URLSearchParams[] {
  const params = [new URLSearchParams(window.location.search)];
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  if (hash.includes('=')) params.push(new URLSearchParams(hash));
  return params;
}

function hasBannedAuthError(): boolean {
  return readAuthErrorParams().some((params) => {
    const code = params.get('error_code')?.toLowerCase() ?? '';
    const description = params.get('error_description')?.toLowerCase() ?? '';
    return code === 'user_banned' || description.includes('banned');
  });
}

function clearAuthErrorFromUrl(): void {
  const url = new URL(window.location.href);
  ['error', 'error_code', 'error_description', 'sb'].forEach((key) => {
    url.searchParams.delete(key);
  });
  url.hash = '';
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

export function AccountClosedHandler() {
  const { signOut } = useAuth();
  const { toast } = useToast();
  const lastHandledRef = useRef<number>(0);
  const DEBOUNCE_MS = 2000;
  const NOTICE_KEY = 'filmple_account_closed_notice';

  useEffect(() => {
    const handleAccountClosed = async () => {
      const now = Date.now();
      if (now - lastHandledRef.current < DEBOUNCE_MS) return;
      lastHandledRef.current = now;

      try {
        sessionStorage.removeItem(NOTICE_KEY);
      } catch {
        // Ignore storage failures
      }

      try {
        await signOut();
      } catch (err) {
        console.error('[AccountClosedHandler] Error signing out:', err);
      }

      toast.error('Your account has been closed. Please contact support if you need assistance.');
    };

    try {
      if (hasBannedAuthError()) {
        clearAuthErrorFromUrl();
        void handleAccountClosed();
      }
      if (sessionStorage.getItem(NOTICE_KEY) === '1') {
        void handleAccountClosed();
      }
    } catch {
      // Ignore storage failures
    }

    window.addEventListener('auth:account-closed', handleAccountClosed);
    return () => window.removeEventListener('auth:account-closed', handleAccountClosed);
  }, [signOut, toast]);

  return null;
}
